import { Dialog as MuiDialog } from '@mui/material/';
import { CloseIcon } from 'assets/icons/CloseIcon';
import { DocumentDownloadIcon } from 'assets/icons/DocumentDownloadIcon';
import axios from 'axios';
import { IconButton } from 'components/IconButton/IconButton';
import { useDeviceCategory } from 'components/Layout/useDeviceCategory';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { saveAs } from 'file-saver';
import { type JSX, ReactNode } from 'react';

import {
  getDialogPaperSx,
  StyledDialogContent,
  StyledDialogHeader,
  TitleContainer,
  TitleIconContainer,
} from './Dialog.style';

export type DialogStyleProps = {
  widthInPercent?: number;
  heightInPercent?: number;
  maxWidthInPx?: number;
};
type Props = {
  isOpen: boolean;
  titleIcon?: ReactNode;
  onClose?: () => void;
  title?: string;
  downloadUrl?: string;
  isPdf?: boolean;
  titleIconColor?: 'grey' | 'orange';
  showCloseIcon?: boolean;
  children: ReactNode;
  responsiveOnTablet?: boolean;
} & DialogStyleProps;

export const Dialog = ({
  isOpen,
  titleIcon,
  onClose,
  title,
  downloadUrl,
  isPdf = false,
  titleIconColor = 'grey',
  showCloseIcon = false,
  widthInPercent,
  heightInPercent,
  maxWidthInPx,
  children,
  responsiveOnTablet = false,
}: Props): JSX.Element => {
  const paperSx = getDialogPaperSx({
    widthInPercent,
    heightInPercent,
    maxWidthInPx,
    responsiveOnTablet,
  });
  const handelDownload = async () => {
    try {
      const response = await axios.get(downloadUrl + '', {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, title);
    } catch (error) {
      console.error('error downloading the pdf file', error);
    }
  };
  const { isMobileAndBigTablet } = useDeviceCategory();

  return (
    <MuiDialog open={isOpen} onClose={onClose} scroll="paper" PaperProps={{ sx: paperSx }}>
      <StyledDialogHeader>
        {isMobileAndBigTablet && isPdf && (
          <Tooltip title="download pdf" id="dounload-button-tooltip">
            <IconButton size="medium" displayStyle="orange" onClick={handelDownload}>
              <DocumentDownloadIcon />
            </IconButton>
          </Tooltip>
        )}
        {titleIcon && (
          <TitleIconContainer $titleIconColor={titleIconColor}>{titleIcon}</TitleIconContainer>
        )}
        <TitleContainer $showCloseIcon={showCloseIcon}>{title}</TitleContainer>
        {onClose && showCloseIcon && (
          <IconButton size="medium" displayStyle="grey" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </StyledDialogHeader>
      <StyledDialogContent>{children}</StyledDialogContent>
    </MuiDialog>
  );
};
